import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import { ModalBase } from '@components/Modal';
import { Select } from '@components/Select';
import { Typography } from '@components/Typography';
import { billingService } from '@services';
import { alertActions } from '@actions';
import { BILLING_TIME_MAP, BILLING_TYPE_MAP } from '@constants/billing';
import { getExpiredAtInfo } from '@helpers/dates';
import { getActiveSubscriptions, formatCurrency, needsSubscribe } from '@helpers/billing';
import { useTranslation } from '@hooks';
import { useOpenUpgradeDrawer } from '@hooks/useOpenUpgradeDrawer';
import { handleApiError } from '@helpers/api';
import DirectoryLogoStrip from '@features/DirectoryLogoStrip';

const Modal = styled(ModalBase)({
    '& .modal-base-paper': {
        width: 420,
        '@media (max-width: 1024px)': { width: '100%' },
    },
    '& .modal-base-content': {
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
    },
});

const PriceBox = styled('div')({
    backgroundColor: 'var(--action-highlight)',
    border: '1px solid var(--divider)',
    borderRadius: 8,
    padding: '12px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
});

const Row = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: 12,
});

const Divider = styled('hr')({
    border: 0,
    borderTop: '1px solid var(--divider)',
    margin: '4px 0',
    width: '100%',
});

const formatMoney = (currency, amount) => formatCurrency(currency, amount, 2);
const formatHeadlinePrice = (currency, amount) => formatCurrency(currency, amount, Number.isInteger(amount) ? 0 : 2);

const DirectoriesAddOnModal = ({ isOpen, location, onClose, onEnrolled }) => {
    const account = useSelector(state => state.account?.account);
    const language = useSelector(state => state.auth.user?.language);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const openUpgradeDrawer = useOpenUpgradeDrawer();

    const subscriptions = getActiveSubscriptions(account).filter(
        item => item.billingPlanName === BILLING_TYPE_MAP.PLUSPOINT,
    );
    const isTrial = needsSubscribe(account);
    const isMultipleStripeAcc = subscriptions.length > 1;

    const [subscriptionId, setSubscriptionId] = useState(subscriptions[0]?.id ?? '');
    const [proration, setProration] = useState(null);
    const [isLoading, setIsLoading] = useState(!isTrial);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (isTrial) {
            return;
        }
        let cancelled = false;
        setIsLoading(true);
        setProration(null);
        billingService
            .previewDirectoriesAddOn({ locationId: location.id, subscriptionId: subscriptionId || null })
            .then(data => {
                if (!cancelled) {
                    setProration(data);
                }
            })
            .catch(error => {
                if (!cancelled) {
                    handleApiError(error);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setIsLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [location.id, subscriptionId, isTrial]);

    const onSubscribeAndAdd = () => {
        onClose();
        openUpgradeDrawer({ directoriesLocation: { id: location.id, name: location.name } });
    };

    const onAdd = async () => {
        setIsSubmitting(true);
        try {
            const updatedLocation = await billingService.enrollDirectoriesAddOn({
                locationId: location.id,
                subscriptionId: subscriptionId || null,
            });
            dispatch(alertActions.success(t('SettingsCompanies.directoriesAddedMsg')));
            await onEnrolled?.(updatedLocation);
            onClose();
        } catch (error) {
            handleApiError(error);
        }
        setIsSubmitting(false);
    };

    const isAnnual = proration?.billingPeriod === BILLING_TIME_MAP.ANNUAL;
    const period = isAnnual ? t('SettingsGeneral.year') : t('SettingsGeneral.month');
    const { expiredAt: nextPaymentAt } = getExpiredAtInfo(proration?.nextPaymentAt, language);
    const chargedImmediately = proration?.chargedImmediately !== false;
    const amountLabel = chargedImmediately
        ? 'SettingsCompanies.chargedTodayProrated'
        : 'SettingsGeneral.proratedOnNextInvoice';

    const primaryActionText = isTrial
        ? t('SettingsCompanies.subscribeAndAddLocation')
        : proration && chargedImmediately
          ? t('SettingsCompanies.addForToday', { amount: formatMoney(proration.currency, proration.amountDue) })
          : t('SettingsCompanies.addLocalDirectories');

    return (
        <Modal
            isOpen={isOpen}
            handleClose={onClose}
            title={t('SettingsCompanies.addLocalDirectories')}
            onPrimaryAction={isTrial ? onSubscribeAndAdd : onAdd}
            primaryActionText={primaryActionText}
            primaryActionDisabled={isSubmitting || (!isTrial && (isLoading || !proration))}
            onSecondaryAction={onClose}
            secondaryActionText={t('buttons.cancel')}
        >
            <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                {t('SettingsCompanies.directoriesValueCopy')}
            </Typography>
            <DirectoryLogoStrip />

            {!isTrial && isMultipleStripeAcc ? (
                <Select
                    label={t('SettingsGeneral.billToSubscription')}
                    value={subscriptionId}
                    onChange={({ target }) => setSubscriptionId(target.value)}
                    options={subscriptions.map(item => ({ value: item.id, label: item.stripeAccountName }))}
                    required
                    fullWidth
                />
            ) : null}

            <PriceBox>
                <Row>
                    <Typography variant="subtitle2">{location.name}</Typography>
                    {!isTrial && proration ? (
                        <Typography variant="subtitle1" sx={{ whiteSpace: 'nowrap' }}>
                            {`${formatHeadlinePrice(proration.currency, proration.unitPrice)} / ${period}`}
                        </Typography>
                    ) : null}
                </Row>
                {isTrial ? (
                    <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                        {t('SettingsCompanies.directoriesRequiresSubscription')}
                    </Typography>
                ) : isLoading ? (
                    <CircularProgress size={20} sx={{ color: 'var(--text-tertiary)', margin: '0 auto' }} />
                ) : proration ? (
                    <>
                        <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                            {t('SettingsCompanies.directoriesMinimumTermNotice')}
                        </Typography>
                        <Divider />
                        <Row>
                            <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                                {t(amountLabel)}
                            </Typography>
                            <Typography variant="subtitle2">
                                {formatMoney(proration.currency, proration.amountDue)}
                            </Typography>
                        </Row>
                        {nextPaymentAt && (
                            <Row>
                                <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                                    {t('SettingsCompanies.thenFrom', { date: nextPaymentAt })}
                                </Typography>
                                <Typography variant="subtitle2">
                                    {`${formatMoney(proration.currency, proration.unitPrice)} / ${period}`}
                                </Typography>
                            </Row>
                        )}
                    </>
                ) : null}
            </PriceBox>
        </Modal>
    );
};

export default DirectoriesAddOnModal;
